import { useState, useMemo } from 'react';
import { faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

const FULL_WIDTH_THRESHOLD = 7;

/**
 * Manages all filter, sort, and grouping logic for the collection / wishlist view.
 * `filteredSets` is a derived value (useMemo) — never duplicated state.
 *
 * @param {Array}   sets        - raw set array from useCollectionData
 * @param {boolean} isWishlist  - suppresses completion/sealed filters on wishlists
 */
export function useFilterSort({ sets, isWishlist }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOption, setSortOption] = useState('theme');
    const [sortDirection, setSortDirection] = useState('desc');
    const [filterOptions, setFilterOptions] = useState({ complete: 'all', sealed: 'all', decade: 'all' });
    const [listSortColumn, setListSortColumn] = useState('name');
    const [listSortDir, setListSortDir] = useState('asc');

    // ── Derived filtered + sorted set list ───────────────────────────────────
    const filteredSets = useMemo(() => {
        let results = [...sets];

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            results = results.filter(s =>
                s.name.toLowerCase().includes(q) ||
                s.set_num.toLowerCase().includes(q) ||
                s.theme_name.toLowerCase().includes(q)
            );
        }

        // Completion filter (collection only)
        if (!isWishlist && filterOptions.complete !== 'all') {
            results = results.filter(s => {
                const c = Number(s.complete), qty = Number(s.quantity);
                if (filterOptions.complete === 'complete')   return c === qty;
                if (filterOptions.complete === 'incomplete') return c < qty;
                if (filterOptions.complete === 'mixed')      return c > 0 && c < qty;
                return true;
            });
        }

        // Sealed filter (collection only)
        if (!isWishlist && filterOptions.sealed !== 'all') {
            results = results.filter(s => {
                const sealed = Number(s.sealed), qty = Number(s.quantity);
                if (filterOptions.sealed === 'sealed') return sealed === qty;
                if (filterOptions.sealed === 'opened') return sealed < qty;
                if (filterOptions.sealed === 'mixed')  return sealed > 0 && sealed < qty;
                return true;
            });
        }

        // Decade filter
        if (filterOptions.decade !== 'all') {
            const decade = parseInt(filterOptions.decade, 10);
            results = results.filter(s => {
                const year = parseInt(s.year, 10);
                return year >= decade && year < decade + 10;
            });
        }

        // Sort
        results.sort((a, b) => {
            switch (sortOption) {
                case 'name':
                    return sortDirection === 'asc'
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);

                case 'year': {
                    const diff = parseInt(a.year, 10) - parseInt(b.year, 10);
                    return sortDirection === 'asc' ? diff : -diff;
                }

                case 'pieces': {
                    const diff = a.num_parts - b.num_parts;
                    return sortDirection === 'asc' ? diff : -diff;
                }

                case 'price': {
                    const diff = (parseFloat(a.retail_price) || 0) - (parseFloat(b.retail_price) || 0);
                    return sortDirection === 'asc' ? diff : -diff;
                }

                case 'value': {
                    const diff = (parseFloat(a.sealed_value) || 0) - (parseFloat(b.sealed_value) || 0);
                    return sortDirection === 'asc' ? diff : -diff;
                }

                case 'theme':
                default: {
                    const cmp = sortDirection === 'asc'
                        ? a.theme_name.localeCompare(b.theme_name)
                        : b.theme_name.localeCompare(a.theme_name);
                    if (cmp !== 0) return cmp;
                    // Secondary sort by year within theme
                    return sortDirection === 'asc'
                        ? parseInt(a.year, 10) - parseInt(b.year, 10)
                        : parseInt(b.year, 10) - parseInt(a.year, 10);
                }
            }
        });

        return results;
    }, [sets, searchQuery, sortOption, sortDirection, filterOptions, isWishlist]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const handleSortChange = (option) => {
        if (sortOption === option) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortOption(option);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (option) => {
        if (sortOption !== option) return null;
        return sortDirection === 'asc' ? faSortUp : faSortDown;
    };

    const resetFilters = () => {
        setSearchQuery('');
        setFilterOptions({ complete: 'all', sealed: 'all', decade: 'all' });
        setSortOption('theme');
        setSortDirection('asc');
    };

    const getDecadeOptions = () => {
        if (sets.length === 0) return [];
        const years = sets.map(s => parseInt(s.year, 10));
        const start = Math.floor(Math.min(...years) / 10) * 10;
        const end   = Math.floor(Math.max(...years) / 10) * 10;
        const decades = [];
        for (let d = start; d <= end; d += 10) decades.push(d);
        return decades;
    };

    /**
     * Groups `setsToGroup` by theme, sorts them, and flags large themes.
     * Returns null when `sortOption !== 'theme'` (used to skip theme grouping in grid view).
     */
    const groupAndSortThemes = (setsToGroup, favoriteThemeId) => {
        if (sortOption !== 'theme') return null;

        const grouped = setsToGroup.reduce((acc, set) => {
            if (!acc[set.theme_id]) {
                acc[set.theme_id] = { theme_id: set.theme_id, theme_name: set.theme_name, sets: [] };
            }
            acc[set.theme_id].sets.push(set);
            return acc;
        }, {});

        const themes = Object.values(grouped);

        themes.sort((a, b) => {
            if (a.theme_id === favoriteThemeId) return -1;
            if (b.theme_id === favoriteThemeId) return  1;
            return sortDirection === 'asc'
                ? a.theme_name.localeCompare(b.theme_name)
                : b.theme_name.localeCompare(a.theme_name);
        });

        themes.forEach(t => { t.isFullWidth = t.sets.length >= FULL_WIDTH_THRESHOLD; });

        return themes;
    };

    // List-view column sort handler
    const handleListSort = (column) => {
        if (listSortColumn === column) {
            setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setListSortColumn(column);
            setListSortDir('asc');
        }
    };

    const sortSetsForList = (setsToSort) =>
        [...setsToSort].sort((a, b) => {
            let aVal, bVal;
            switch (listSortColumn) {
                case 'set_num':      aVal = a.set_num;                       bVal = b.set_num;                       break;
                case 'year':         aVal = Number(a.year) || 0;             bVal = Number(b.year) || 0;             break;
                case 'num_parts':    aVal = Number(a.num_parts) || 0;        bVal = Number(b.num_parts) || 0;        break;
                case 'retail_price': aVal = Number(a.retail_price) || 0;     bVal = Number(b.retail_price) || 0;     break;
                case 'sealed_value': aVal = Number(a.sealed_value) || 0;     bVal = Number(b.sealed_value) || 0;     break;
                case 'used_value':   aVal = Number(a.used_value) || 0;       bVal = Number(b.used_value) || 0;       break;
                case 'name':
                default:             aVal = (a.name || '').toLowerCase();    bVal = (b.name || '').toLowerCase();    break;
            }
            if (listSortDir === 'asc') return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        });

    return {
        filteredSets,
        searchQuery,
        setSearchQuery,
        sortOption,
        setSortOption,
        sortDirection,
        setSortDirection,
        filterOptions,
        setFilterOptions,
        listSortColumn,
        listSortDir,
        handleSortChange,
        getSortIcon,
        resetFilters,
        getDecadeOptions,
        groupAndSortThemes,
        handleListSort,
        sortSetsForList,
    };
}
